import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VideoAssetProps } from 'blocks/Timeline/components/VideoAsset';
import { millisToPosition, positionToMillis } from 'blocks/Timeline/utils';
import {
  debouncedSaveConfiguration,
  updateVideo,
} from 'redux/modules/embed/actions';
import {
  embedSlideIdsSelector,
  getVideoSelector,
  videoIdsSelector,
} from 'redux/modules/embed/selectors';
import { Dispatch } from 'redux/types';
import { MINIMUM_SLIDE_DURATION_MILLIS } from 'utils/constants';

export type UseVideoClipAssetResult = Required<
  Pick<
    VideoAssetProps,
    | 'fullDurationWidth'
    | 'minWidth'
    | 'onDrag'
    | 'onDragStop'
    | 'onResize'
    | 'onResizeStop'
    | 'order'
    | 'playFromOffset'
    | 'position'
    | 'posterSrc'
    | 'scaling'
    | 'width'
  >
>;

export type UseVideoClipAssetProps = Pick<VideoAssetProps, 'pxPerSec' | 'id'>;

export default function useVideoClipAsset({ id, pxPerSec }) {
  const dispatch = useDispatch<Dispatch>();
  const video = useSelector(getVideoSelector(id));
  const nSlides = useSelector(state => {
    const ids = embedSlideIdsSelector(state);
    return ids?.length ?? 0;
  });

  const fullDurationWidth = !video
    ? 0
    : millisToPosition(video.sourceDurationMillis, pxPerSec);

  const minWidth = millisToPosition(MINIMUM_SLIDE_DURATION_MILLIS, pxPerSec);

  const order = useSelector(state => {
    const videoIds = videoIdsSelector(state);
    return !videoIds || !id ? undefined : nSlides + videoIds.indexOf(id) + 1;
  });

  const playFromOffset = !video
    ? 0
    : millisToPosition(video.playFromMillis || 0, pxPerSec);

  const position = !video ? 0 : millisToPosition(video.startMillis, pxPerSec);

  const posterSrc = video?.previewThumbnailUrl;

  const scaling = video?.scaling;

  const width = !video
    ? 0
    : millisToPosition(video.endMillis - video.startMillis, pxPerSec);

  const onDrag = useCallback(
    data => {
      dispatch(
        updateVideo(id, {
          endMillis: positionToMillis(data.x + data.width, pxPerSec),
          id,
          startMillis: positionToMillis(data.x, pxPerSec),
        }),
      );
    },
    [dispatch, id, pxPerSec],
  );

  const onDragStop = useCallback(
    (_, moved) => {
      if (moved) {
        dispatch(debouncedSaveConfiguration());
      }
    },
    [dispatch],
  );

  const onResize = useCallback(
    (newPlayFromOffset, newWidth, _, pos) => {
      dispatch(
        updateVideo(id, {
          endMillis: positionToMillis(pos + newWidth, pxPerSec),
          id,
          playFromMillis: positionToMillis(newPlayFromOffset, pxPerSec),
          startMillis: positionToMillis(pos, pxPerSec),
        }),
      );
    },
    [dispatch, id, pxPerSec],
  );

  const onResizeStop = useCallback(() => {
    dispatch(debouncedSaveConfiguration());
  }, [dispatch]);

  return {
    fullDurationWidth,
    minWidth,
    onDrag,
    onDragStop,
    onResize,
    onResizeStop,
    order,
    playFromOffset,
    position,
    posterSrc,
    scaling,
    width,
  };
}
